import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '@rocco/components/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@rocco/components/select';
import type { StepContentProps } from '~/ui-rocco/multi-step-form';
import { FormHeader } from '~/ui-rocco/multi-step-form/FormHeader';
import { isBudgetRange } from '@modules/build-hub/utils/format-budget';
import { BudgetDataSchema } from '@modules/build-hub-creator/validation/schemas';
import {
    BUDGET_OPTIONS,
    DEFAULT_BUDGET_OPTION,
    CUSTOM_BUDGET_OPTION,
    type BudgetOption,
} from '@modules/build-hub-creator/configs/constants';
import { mapBudgetMinMaxToRange } from '@modules/build-hub/data-mappers/map-budget-min-max-to-range';
import type { StageConfiguration } from '@modules/build-hub/configs/stage-config';
import type { BudgetRange } from '@modules/build-hub-creator/api/types';

type BudgetStepProps = Pick<
    StepContentProps,
    'onDataChange' | 'initialData' | 'setIsValid'
> & {
    isEditForm?: boolean;
    formHeader?: React.ReactNode;
    projectScope?: string;
    stageConfig?: StageConfiguration;
};

type ProjectBudgetInitialData = BudgetRange & {
    projectScope?: string;
    stageConfig?: StageConfiguration;
};

export function ProjectBudgetStep({
    onDataChange,
    initialData,
    setIsValid,
    isEditForm,
    formHeader,
    projectScope,
    stageConfig,
}: BudgetStepProps) {
    const normalizedInitialData =
        initialData && typeof initialData === 'object'
            ? (initialData as ProjectBudgetInitialData)
            : ({} as ProjectBudgetInitialData);

    const effectiveProjectScope =
        projectScope ?? normalizedInitialData.projectScope;

    const effectiveStageConfig =
        stageConfig ?? normalizedInitialData.stageConfig;

    // Get budget options specific to the project scope, or fall back to default
    const budgetOptions = useMemo<BudgetOption[]>(() => {
        if (!effectiveProjectScope || !effectiveStageConfig) {
            return BUDGET_OPTIONS;
        }

        // Search in both residential and commercial scopes
        const allScopes = [
            ...(effectiveStageConfig.residential || []),
            ...(effectiveStageConfig.commercial || []),
        ];

        const scopeConfig = allScopes.find(
            (scope) =>
                scope.value === effectiveProjectScope ||
                scope.label === effectiveProjectScope
        );

        if (scopeConfig?.budgetOptions?.length) {
            // Map backend budget options to frontend BudgetOption format
            return scopeConfig.budgetOptions.map((opt) => ({
                key: opt.key,
                label: opt.label,
                min: opt.min ?? undefined,
                max: opt.max ?? undefined,
            }));
        }

        // Fallback to default if scope not found or has no budget options
        return BUDGET_OPTIONS;
    }, [effectiveProjectScope, effectiveStageConfig]);

    // Find the custom budget option from the current budget options
    const customBudgetOption = useMemo(
        () =>
            budgetOptions.find((opt) => opt.key === 'custom') ||
            CUSTOM_BUDGET_OPTION,
        [budgetOptions]
    );

    // type guard unknown initialData
    const savedBudgetRange = isBudgetRange(initialData)
        ? initialData
        : undefined;
    // BudgetOption from initialData
    const selectedBudget = savedBudgetRange
        ? mapBudgetMinMaxToRange(savedBudgetRange, budgetOptions)
        : DEFAULT_BUDGET_OPTION;
    const [selectedKey, setSelectedKey] = useState<string>(selectedBudget.key);
    const isInitialMount = useRef(true);

    // Update selectedKey when budgetOptions or savedBudgetRange changes
    // This ensures the correct option is selected when the component initializes
    // or when budget options are loaded asynchronously
    // We don't include selectedKey in deps to avoid overriding user selections
    useEffect(() => {
        const newSelectedBudget = savedBudgetRange
            ? mapBudgetMinMaxToRange(savedBudgetRange, budgetOptions)
            : DEFAULT_BUDGET_OPTION;
        // Only update if the computed budget key differs from current selection
        // This handles initial load and async data loading without overriding user changes
        setSelectedKey((currentKey) => {
            if (currentKey !== newSelectedBudget.key) {
                return newSelectedBudget.key;
            }
            return currentKey;
        });
    }, [budgetOptions, savedBudgetRange]);

    const { reset, watch, register, setValue, formState } = useForm<
        z.infer<typeof BudgetDataSchema>
    >({
        resolver: zodResolver(BudgetDataSchema),
        mode: 'onChange',
        defaultValues: savedBudgetRange ?? {
            min: DEFAULT_BUDGET_OPTION.min,
            max: DEFAULT_BUDGET_OPTION.max,
        },
    });

    // Register fields without rendering them
    useEffect(() => {
        register('min', { valueAsNumber: true });
        register('max', { valueAsNumber: true });
    }, [register]);

    const { min, max } = watch();

    // Populate budget range from select option
    useEffect(() => {
        const opt = budgetOptions.find((o) => o.key === selectedKey);
        if (!opt || opt.key === customBudgetOption.key) {
            // For custom budget, preserve saved values if available (especially on initial mount)
            if (
                savedBudgetRange &&
                typeof savedBudgetRange.min === 'number' &&
                typeof savedBudgetRange.max === 'number' &&
                savedBudgetRange.min === savedBudgetRange.max &&
                savedBudgetRange.min > 0
            ) {
                // On initial mount or when switching to custom, preserve the saved custom budget value
                const currentMin = watch('min');
                const currentMax = watch('max');
                if (
                    isInitialMount.current ||
                    currentMin !== savedBudgetRange.min ||
                    currentMax !== savedBudgetRange.max
                ) {
                    reset({
                        min: savedBudgetRange.min,
                        max: savedBudgetRange.max,
                    });
                }
                isInitialMount.current = false;
            } else {
                // reset to NaN for numeric input to register if no saved custom value
                reset({ min: NaN, max: NaN });
                isInitialMount.current = false;
            }
        } else {
            reset({ min: opt.min ?? NaN, max: opt.max ?? NaN });
            isInitialMount.current = false;
        }
    }, [
        selectedKey,
        reset,
        budgetOptions,
        customBudgetOption,
        savedBudgetRange,
        watch,
    ]);

    useEffect(() => {
        if (savedBudgetRange) {
            const { min: initialMin, max: initialMax } = savedBudgetRange;
            if (initialMin !== min || initialMax !== max) {
                onDataChange?.({ min, max });
            }
        } else {
            onDataChange?.({ min, max });
        }
    }, [min, max, onDataChange, savedBudgetRange]);

    // Validate step and notify parent
    useEffect(() => {
        const isValid = formState.isValid;
        setIsValid?.(isValid);
    }, [formState.isValid, setIsValid]);

    const labelClass = isEditForm
        ? 'mb-2 block text-sm font-medium text-gray-700'
        : 'mb-2 block text-base font-medium text-gray-900';

    return (
        <div>
            {formHeader ?? (
                <FormHeader
                    title="Project Budget"
                    description="Tailor suggestions and connect with appropriate resources."
                />
            )}

            <div className={isEditForm ? 'space-y-4' : ''}>
                <div>
                    <label className={labelClass}>Budget Range</label>
                    <Select value={selectedKey} onValueChange={setSelectedKey}>
                        <SelectTrigger className="w-full" color="gray">
                            <SelectValue placeholder="Select your budget range" />
                        </SelectTrigger>
                        <SelectContent>
                            {budgetOptions.map((opt) => (
                                <SelectItem key={opt.key} value={opt.key}>
                                    {opt.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {selectedKey === customBudgetOption.key && (
                    <div className="mt-4">
                        <label htmlFor="custom-budget" className={labelClass}>
                            Custom Budget Amount
                        </label>
                        <Input
                            id="max"
                            type="number"
                            color="gray"
                            text="default"
                            placeholder="e.g. 7,500,000"
                            {...register('max', {
                                valueAsNumber: true,
                                onChange: (e) =>
                                    setValue('min', e.target.valueAsNumber),
                            })}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
